/**
 * Range/set helpers used by the split modal. Kept out of the component so
 * they're easy to unit-test and shared between the text-input parser and
 * the click-driven page toggles.
 *
 * The canonical shape in the modal is a `Set<number>` per part — text is
 * a formatted view of that set, and the server payload is a compressed
 * `[[start, end], …]` derived from the set at submit time.
 */

/** Parse "1-3, 5, 8-10" into a Set of page numbers. Rejects invalid input. */
export function parseRangesToPages(text: string): {
  pages: Set<number>;
  error: string | null;
} {
  const trimmed = text.trim();
  if (!trimmed) {
    return { pages: new Set(), error: null };
  }
  const pages = new Set<number>();
  const parts = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  for (const part of parts) {
    const dashMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
    const soloMatch = part.match(/^(\d+)$/);
    if (dashMatch) {
      const start = parseInt(dashMatch[1], 10);
      const end = parseInt(dashMatch[2], 10);
      if (start < 1 || end < 1 || start > end) {
        return { pages: new Set(), error: `Invalid range "${part}"` };
      }
      for (let n = start; n <= end; n++) pages.add(n);
    } else if (soloMatch) {
      const n = parseInt(soloMatch[1], 10);
      if (n < 1) return { pages: new Set(), error: `Invalid page "${part}"` };
      pages.add(n);
    } else {
      return { pages: new Set(), error: `Cannot parse "${part}"` };
    }
  }
  return { pages, error: null };
}

/**
 * Compress a set of page numbers into the shortest human-readable
 * comma-and-dash string: `{1,2,3,5,8,9,10}` → `"1-3, 5, 8-10"`.
 * This is what the text input shows after a click updates the set.
 */
export function pagesToRangesText(pages: ReadonlySet<number>): string {
  return pageSetToRanges(pages)
    .map(([start, end]) => (start === end ? String(start) : `${start}-${end}`))
    .join(", ");
}

/**
 * Compress a page set into `[[start, end], …]` — the server payload shape.
 * Ranges are inclusive on both ends and non-overlapping.
 */
export function pageSetToRanges(
  pages: ReadonlySet<number>,
): Array<[number, number]> {
  if (pages.size === 0) return [];
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const ranges: Array<[number, number]> = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const n = sorted[i];
    if (n === prev + 1) {
      prev = n;
      continue;
    }
    ranges.push([start, prev]);
    start = n;
    prev = n;
  }
  ranges.push([start, prev]);
  return ranges;
}

/**
 * Colors cycle after PALETTE.length parts. Chosen for contrast on both
 * light and dark modal backgrounds and to avoid confusion with existing
 * status colors (green completed, red failed).
 */
export const PART_PALETTE: readonly string[] = [
  "#58a6ff", // blue
  "#10b981", // green
  "#f97316", // orange
  "#a78bfa", // purple
  "#facc15", // yellow
  "#38bdf8", // cyan
  "#f472b6", // pink
  "#4ade80", // lime
];

export function colorForPartIndex(idx: number): string {
  return PART_PALETTE[idx % PART_PALETTE.length];
}
